/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import {
  bbox as geometryBounds,
  bboxPolygon,
  booleanIntersects,
  centroid,
  coordAll,
} from '@turf/turf';
import type { Feature, FeatureCollection, Polygon } from 'geojson';
import { nifcDataChunksAtom, useNifcPerimeterDetail, useSetNifcDataChunks } from './selectors';
import { NifcPerimeterProperties, NifcPostProperties } from './types';
import type { RGBAColor } from '../map/deckgl-types';
import { POST_EVENT_OF, primaryKeyForSection } from '../Post/PostLayer';
import { useGridCellLayer } from '../map/GridCellLayer';
import type { Bbox } from '../map/geometryDetail';
import { debouncedViewStateAtom } from '../map/atoms';
import { useAtom, useAtomValue } from 'jotai';
import React from 'react';
import { useMatch } from 'react-router-dom';
import { TextLayer } from '@deck.gl/layers';
import { selectedFeatureAtom } from '../Incident/FeaturePopup';

type Props = {
  extruded: boolean;
  visible: boolean;
};

const VISIBLE_ZOOM = 7;

/**
 * How the two resolutions of nifc_bounds are merged.
 *
 * The rows carry nothing to merge on: no key column, a nullable createdate, and
 * the workflow writes the literal name "None" for every unnamed perimeter, so name
 * plus date collapses distinct fires onto one value and dropping by such a key
 * deletes perimeters the detail set never replaced.
 *
 * A base perimeter is dropped when either test says the detail set accounts for
 * it: position (isInFetchedViewport) or identity (isSameRowAsDetail). Together
 * they keep any fire from being drawn twice while one the detail fetch did not
 * return keeps its existing geometry -- with the residual case noted below.
 */

/**
 * Whether the perimeter's geometry reaches into the box the detail set was
 * fetched over.
 *
 * Mirrors the server's filter -- ST_Intersects against the full-resolution
 * geometry whichever variant was requested -- so it selects what the detail fetch
 * replaced. Envelope first, then the geometry, since a bent perimeter can have an
 * envelope over the box with none of itself inside. Comparing position rather than
 * identity is what survives the two sets coming from different workflow runs.
 *
 * Applied to simplified geometry while the server filtered stored geometry, which
 * differ by up to ~73 m, so the two disagree in a narrow band at the box edge.
 * Where full reaches the box and simplified does not, isSameRowAsDetail catches
 * it. The reverse is residual: such a perimeter is dropped though the detail set
 * never returned it, until a later fetch covers it. The band sits on the padded
 * box edge, off screen while the viewport is centred, but a pan reuses the box so
 * the edge can come to rest on it. Closing this needs a row identity the table
 * does not have.
 */
const isInFetchedViewport = (
  feature: Feature<Polygon, NifcPerimeterProperties>,
  [minLng, minLat, maxLng, maxLat]: Bbox,
  fetchedArea: Feature<Polygon>
): boolean => {
  const [west, south, east, north] = geometryBounds(feature);
  if (west > maxLng || east < minLng || south > maxLat || north < minLat) {
    return false;
  }
  return booleanIntersects(feature, fetchedArea);
};

/** Every coordinate of a perimeter, as keys that compare exactly. */
const vertexKeys = (feature: Feature<Polygon, NifcPerimeterProperties>): string[] =>
  coordAll(feature).map(([longitude, latitude]) => `${longitude},${latitude}`);

/**
 * Whether the perimeter is the same row as one in the detail set, read through the
 * other geometry column.
 *
 * Exact, not a proximity guess: `geom_simplified` is
 * ST_SimplifyPreserveTopology (migration 000026), i.e. Douglas-Peucker, which
 * keeps a subset of the input vertices and never moves one, and both columns are
 * serialized by the same ST_AsGeoJSON call -- so every coordinate of the
 * simplified geometry appears, bit for bit, in the full-resolution geometry of
 * that row. A perimeter whose stored geometry reaches into the fetched box is in
 * the detail set, so this catches it even when its simplified geometry falls
 * outside the box; without it, that perimeter was kept AND its detail copy added,
 * drawing the fire twice with two labels stacked on one centroid.
 *
 * Should the generated column ever stop retaining input vertices, this test simply
 * stops matching and the merge falls back to the positional test alone -- the
 * behaviour it had before, not something worse.
 */
const isSameRowAsDetail = (
  feature: Feature<Polygon, NifcPerimeterProperties>,
  detailVertices: Set<string>
): boolean => {
  const keys = vertexKeys(feature);
  return keys.length > 0 && keys.every(key => detailVertices.has(key));
};

export const useNifcLayer = (props: Props) => {
  // A one-shot latch rather than state: the fetch must happen once per mount,
  // and a state flag would re-render the map for a value nothing renders.
  const perimetersRequested = React.useRef(false);
  const [viewState] = useAtom(debouncedViewStateAtom);
  const [selectedPost] = useAtom(selectedFeatureAtom);
  const primaryKey = useAtomValue(primaryKeyForSection('nifc-post'));

  const nifcPerimeters = useAtomValue(nifcDataChunksAtom<NifcPerimeterProperties>('perimeter'));
  const perimetersCallback = useSetNifcDataChunks('perimeter', nifcPerimeters);
  const match = useMatch({ path: '/' });

  React.useEffect(() => {
    if (!match || perimetersRequested.current) return;
    perimetersRequested.current = true;
    perimetersCallback();
  }, [perimetersCallback, match]);

  // Zoomed past the simplified column's fidelity ceiling the perimeters on
  // screen come back at full resolution for the viewport. The rest of the
  // national set keeps its simplified geometry rather than being dropped, so
  // panning never leaves a fire unoutlined while the next viewport fetch runs.
  const perimeterDetail = useNifcPerimeterDetail<NifcPerimeterProperties>(props.visible);

  const perimeterChunks = React.useMemo((): FeatureCollection<
    Polygon,
    NifcPerimeterProperties
  >[] => {
    if (perimeterDetail == null) {
      return nifcPerimeters;
    }
    const fetchedArea = bboxPolygon(perimeterDetail.bbox);
    const detailVertices = new Set(
      perimeterDetail.collection.features.flatMap(feature => vertexKeys(feature))
    );
    return [
      ...nifcPerimeters
        .map(chunk => ({
          ...chunk,
          features: chunk.features.filter(
            feature =>
              !isInFetchedViewport(feature, perimeterDetail.bbox, fetchedArea) &&
              !isSameRowAsDetail(feature, detailVertices)
          ),
        }))
        .filter(chunk => chunk.features.length > 0),
      perimeterDetail.collection,
    ];
  }, [nifcPerimeters, perimeterDetail]);

  const postRequested = React.useRef(false);
  const nifcPost = useAtomValue(nifcDataChunksAtom<NifcPostProperties>('post'));
  const postCallback = useSetNifcDataChunks('post', nifcPost);

  React.useEffect(() => {
    if (!props.visible || postRequested.current) return;
    postRequested.current = true;
    postCallback();
  }, [postCallback, props.visible]);

  const fillColor = React.useMemo(
    () => (primaryKey === 'priority_rank' ? getPostFillColor : undefined),
    [primaryKey]
  );

  const NifcGridCellLayer = useGridCellLayer<Feature<Polygon, NifcPostProperties>>({
    id: 'nifc-post',
    // @ts-expect-error
    data: nifcPost,
    getFillColor: fillColor,
    eventOf: POST_EVENT_OF.nifc,
    extruded: props.extruded,
    visible: props.visible,
    highlightId: selectedPost?.id,
  });

  const NifcPerimeterLayer = useGridCellLayer<Feature<Polygon, NifcPerimeterProperties>>({
    id: 'nifc-perimeter',
    // @ts-expect-error
    data: perimeterChunks,
    pickable: true,
    getFillColor: perimeterFillColor,
    extruded: false,
    visible: props.visible,
  });

  const nifcLabelLayer = new TextLayer<Feature<Polygon, NifcPerimeterProperties>>({
    id: 'nifc-text-layer',
    data: perimeterChunks.flatMap(p => p.features),
    pickable: false,
    getPosition: d => {
      const post = centroid(d).geometry.coordinates;
      return [post[0], post[1]];
    },
    getText: d => {
      return d.properties?.incidentname;
    },
    billboard: true,
    getSize: 12,
    getAngle: 0,
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'center',
    visible: viewState?.zoom && viewState.zoom > VISIBLE_ZOOM ? props.visible : false,
  });

  return [NifcPerimeterLayer, NifcGridCellLayer, nifcLabelLayer];
};
const perimeterFillColor = [181, 9, 9, 130] as RGBAColor;
// const perimeterLineColor = [181, 9, 9, 200] as RGBAColor;

const postColorRange: RGBAColor[] = [
  [181, 9, 9, 190], // #B50909
  [219, 129, 0, 190], // #DB8100
  [226, 178, 0, 190], // #E2B200
  [143, 195, 201, 190], // #8FC3C9
  [95, 133, 188, 190], // #5F85BC
];

export const getPostFillColor = (d: Feature<Polygon, NifcPostProperties>): RGBAColor => {
  const { priority_rank } = d.properties;
  switch (priority_rank) {
    case 1:
      return postColorRange[0];
    case 2:
      return postColorRange[1];
    case 3:
      return postColorRange[2];
    case 4:
      return postColorRange[3];
    case 5:
      return postColorRange[4];
    default:
      return [0, 0, 0, 0];
  }
};
